import {
  LoanPaymentType,
  type LoanContract,
  type LoanPayment,
  type LoanPaymentTransaction,
  type MonthGroupedItems,
  type NextPaymentInfo,
} from '@/features/loan';
import { CreditProductType, type CreditOffer } from '@/features/offer/types';
import { formatDate } from 'date-fns';

/**
 * Returns the 15th day of the next month from today.
 * @returns {Date} The calculated date
 */
export const defaultBillDate = (): Date => {
  const today = new Date();
  const resultDate = new Date(today);

  // Move to next month
  if (resultDate.getMonth() === 11) {
    // December
    resultDate.setFullYear(resultDate.getFullYear() + 1);
    resultDate.setMonth(0); // January
  } else {
    resultDate.setMonth(resultDate.getMonth() + 1);
  }
  // Set the day to 15
  resultDate.setDate(15);

  return resultDate;
};

export const calculateDegreeOfIndebtedness = (
  offer: CreditOffer,
  requestedAmount: number,
  noOfRates: number
): number => {
  let degreeOfIndebtedness =
    offer.bnrLimitPercentage > 0 ? offer.bnrLimitPercentage : 40.0;

  if (offer.salary < 1) {
    return degreeOfIndebtedness;
  }

  let annuity: number;
  if (
    offer.type !== CreditProductType.CreditLine &&
    offer.type !== CreditProductType.WhiteLabelCard
  ) {
    annuity = calculateAnnuity(
      requestedAmount,
      offer.interestDailyPercentage,
      offer.paymentScheduleDuration,
      noOfRates
    );
  } else if (offer.type === CreditProductType.WhiteLabelCard) {
    annuity =
      requestedAmount * 0.05 +
      (offer.interestDailyPercentage / 100) *
        offer.paymentScheduleDuration *
        requestedAmount;
  } else {
    annuity =
      requestedAmount * 0.05 + offer.costPerDay * offer.paymentScheduleDuration;
  }
  degreeOfIndebtedness =
    ((offer.monthlyPayments + annuity) / offer.salary) * 100.0;
  return degreeOfIndebtedness;
};

function calculateAnnuity(
  principal: number,
  dailyInterestPercentage: number,
  paymentScheduleDuration: number,
  numberOfRates: number
): number {
  if (
    principal < 1 ||
    dailyInterestPercentage < 0.0001 ||
    paymentScheduleDuration < 1 ||
    numberOfRates < 1
  )
    return 0;
  const monthlyInterestPercentage =
    dailyInterestPercentage * paymentScheduleDuration;
  const rateInterest = monthlyInterestPercentage / 100;
  return (
    rateInterest *
    (principal / (1 - Math.pow(1 + rateInterest, -numberOfRates)))
  );
}

export const getMoneySendPayment = (
  contract: LoanContract
): LoanPayment | undefined => {
  const moneySend = contract?.moneySend;

  if (!moneySend) {
    return undefined;
  }

  return {
    id: moneySend.id,
    paymentType: LoanPaymentType.MoneySend,
    amount: moneySend.amount,
    status: moneySend.status,
    timestamp: moneySend.timestamp,
    currency: 'lei',
  } as LoanPayment;
};

export const getLoanPayments = (contract: LoanContract): LoanPayment[] => {
  const moneySend = getMoneySendPayment(contract);

  if (!moneySend) {
    return contract?.payments ?? [];
  }
  return [...(contract?.payments ?? []), moneySend];
};

export const convertToPaymentTransaction = (
  payment: LoanPayment
): LoanPaymentTransaction => {
  return {
    id: payment.id,
    orderId: payment.orderId,
    orderNo: payment.orderNo,
    merchantCui: payment.merchantCui,
    amount: payment.amount,
    status: payment.status,
    timestamp: payment.timestamp,
    type: payment.paymentType,
    currency: payment.currency || 'lei',
    sign: payment.amountSign || '',
    merchantName: payment.merchantName,
  };
};

export const getLoanPaymentsTransactions = (
  payments: LoanPayment[]
): LoanPaymentTransaction[] => {
  return payments.map(convertToPaymentTransaction);
};

export const getGroupedPayments = (
  transactions: LoanPayment[]
): MonthGroupedItems<LoanPaymentTransaction>[] => {
  if (!transactions) return [];
  const groupedData = transactions.reduce<
    Record<string, MonthGroupedItems<LoanPaymentTransaction>>
  >((groups, item) => {
    const monthYear = formatDate(item.timestamp || new Date(), 'MMMM • YYYY');

    if (!groups[monthYear]) {
      groups[monthYear] = {
        month: monthYear,
        summary: '',
        items: [],
      };
    }

    const paymentTransaction = convertToPaymentTransaction(item);
    groups[monthYear].items.push(paymentTransaction);
    groups[monthYear].summary = ``;
    return groups;
  }, {});
  return Object.keys(groupedData).map((key) => groupedData[key]);
};

export const getNextPaymentInfo = (
  contract: LoanContract | undefined
): NextPaymentInfo | null => {
  if (!contract) {
    return null;
  }

  // Find the next unpaid installment
  const nextInstallment = contract.debtGroups?.find(
    (group) => !group.isPaid && group.isNext === true
  );

  // Filter the next payments that are due before or on the due date of nextInstallment
  const nextPayments = [
    ...(contract.debtGroups?.filter(
      (group) =>
        !group.isPaid && group.overdueHours >= 0 && group.isNext === false
    ) || []),
    ...(nextInstallment ? [nextInstallment] : []),
  ];

  // Aggregate all required values in one loop
  const aggregatedPayments = nextPayments.reduce(
    (totals, group) => {
      const capital = group.total - group.interest - group.penalty;
      return {
        totalAmount: totals.totalAmount + group.total,
        capital: totals.capital + capital,
        interest: totals.interest + group.interest,
        penalties: totals.penalties + group.penalty,
      };
    },
    { totalAmount: 0, capital: 0, interest: 0, penalties: 0 } // Initialize totals
  );

  const lastDate = nextPayments[nextPayments.length - 1]?.dueDate;

  return {
    ...aggregatedPayments,
    date: lastDate || contract.nextPaymentDate,
  };
};
